import Text from "../../../Text";
import {
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../../ui/dialog.tsx";
import { useEffect, type FC } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../../ui/form.tsx";
import { Button } from "../../../ui/button.tsx";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group.tsx";
import { generateClient } from "aws-amplify/api";
import { skipOnboarding } from "@/graphql/mutations.ts";
import { useNavigate } from "react-router";
import { triggerEvent } from "@/lib/utils.ts";
import { useTranslation } from "react-i18next";

const formSchema = z.object({
  job: z.string(),
});

const client = generateClient();

type Props = {
  onChangeValue: (name: string, value: string | string[]) => void;
};

const StepOne: FC<Props> = ({ onChangeValue }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
  });

  const handleSubmit = (values: z.infer<typeof formSchema>) => {
    onChangeValue("job", values.job);
  };

  useEffect(() => {
    triggerEvent("Onboarding Step Viewed", {
      step_name: "Step One - Job Role",
      step_index: 1,
    });

    return () => {
      triggerEvent("Onboarding Step Completed", {
        step_name: "Step One - Job Role",
        step_index: 1,
      });
    };
  }, []);

  return (
    <DialogContent showCloseButton={false}>
      <VisuallyHidden>
        <DialogTitle>Onboarding</DialogTitle>
      </VisuallyHidden>
      <DialogHeader>
        <VisuallyHidden>
          <DialogDescription>Onboarding, what's your job</DialogDescription>
        </VisuallyHidden>
        <img
          src="/svg/krunsh-logo.svg"
          alt="krunsh logo"
          height={36}
          width={120}
        />
        <div className="flex flex-col gap-[4px]">
          <Text fontWeight={600} fontSize={20} color="#44546F">
            {t("start_with_krunsh")}
          </Text>
          <Text fontWeight={400} fontSize={14} color="#44546F">
            {t("start_with_onboarding")}
          </Text>
        </div>

        <div className="flex flex-col mb-[24px] mt-[36px]">
          <Text fontSize={12} fontWeight={400}>
            {t("onboarding_step_1_title")}
          </Text>
          <Text fontSize={20} fontWeight={600}>
            {t("onboarding_step_1_description")}
          </Text>
        </div>
      </DialogHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <FormField
            control={form.control}
            name="job"
            render={({ field }) => (
              <FormItem className="space-y-[12px]">
                <FormControl>
                  <RadioGroup
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    className="flex flex-col w-full"
                  >
                    <FormItem className="flex items-center w-full border border-[#E9D8D8] rounded-[8px] p-[12px]">
                      <FormControl>
                        <RadioGroupItem value="independent" />
                      </FormControl>
                      <FormLabel>
                        <Text fontSize={14} fontWeight={400}>
                          {t("job_independent")}
                        </Text>
                      </FormLabel>
                    </FormItem>
                    <FormItem className="flex items-center w-full border border-[#E9D8D8] rounded-[8px] p-[12px]">
                      <FormControl>
                        <RadioGroupItem value="investor" />
                      </FormControl>
                      <FormLabel>
                        <Text fontSize={14} fontWeight={400}>
                          {t("job_investor")}
                        </Text>
                      </FormLabel>
                    </FormItem>
                    <FormItem className="flex items-center w-full border border-[#E9D8D8] rounded-[8px] p-[12px]">
                      <FormControl>
                        <RadioGroupItem value="marketing_director" />
                      </FormControl>
                      <FormLabel>
                        <Text fontSize={14} fontWeight={400}>
                          {t("job_marketing_director")}
                        </Text>
                      </FormLabel>
                    </FormItem>
                    <FormItem className="flex items-center w-full border border-[#E9D8D8] rounded-[8px] p-[12px]">
                      <FormControl>
                        <RadioGroupItem value="sales_director" />
                      </FormControl>
                      <FormLabel>
                        <Text fontSize={14} fontWeight={400}>
                          {t("job_sales_director")}
                        </Text>
                      </FormLabel>
                    </FormItem>
                    <FormItem className="flex items-center w-full border border-[#E9D8D8] rounded-[8px] p-[12px]">
                      <FormControl>
                        <RadioGroupItem value="ceo" />
                      </FormControl>
                      <FormLabel>
                        <Text fontSize={14} fontWeight={400}>
                          CEO
                        </Text>
                      </FormLabel>
                    </FormItem>
                    <FormItem className="flex items-center w-full border border-[#E9D8D8] rounded-[8px] p-[12px]">
                      <FormControl>
                        <RadioGroupItem value="others" />
                      </FormControl>
                      <FormLabel>
                        <Text fontSize={14} fontWeight={400}>
                          {t("others")}
                        </Text>
                      </FormLabel>
                    </FormItem>
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="grid grid-cols-2 gap-[16px]">
            <Button
              size="lg"
              type="button"
              variant="secondaryFilled"
              className="w-full mt-[24px]"
              onClick={async () => {
                await client.graphql({
                  query: skipOnboarding,
                });

                navigate("/");
              }}
            >
              {t("skip_onboarding")}
            </Button>

            <Button
              size="lg"
              type="submit"
              variant="primaryFilled"
              className="w-full mt-[24px]"
              disabled={!form.formState.isValid}
            >
              {t("next")}
            </Button>
          </div>
        </form>
      </Form>
    </DialogContent>
  );
};

export default StepOne;
